import { Box, Checkbox, TableCell, TableRow } from '@material-ui/core';
import {
  AgentInLenderContext,
  formatMoney,
  Lender,
  LenderMap,
  LenderName,
  PaymentMethodType,
  Pricing,
  PricingInsurance,
} from '@nemuru-eng/js-api-client';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { getLendersFromPricing } from '@/components/modules/AgentDetail/ProvidersDetails/components/AgentPricings/utils/getLendersFromPricing';
import { Font } from '@/components/shared/components/Font';
import { LenderIconAvatar } from '@/components/shared/components/Logo/LenderLogo/LenderIcon';
import { MoreActionsMenuWithIcon } from '@/components/shared/components/MoreActionsMenu/MoreActionsMenuWithIcon';
import { ActionsOption } from '@/components/shared/components/MoreActionsMenu/types/ActionsOption';
import { PricingInsuranceChip } from '@/components/shared/components/PricingsTable/components/PricingInsuranceChip';
import { IconByAllowedPaymentMethodType } from '@/components/shared/components/PricingsTable/constants/IconByAllowedPaymentMethodType';
import { usePricingsTable } from '@/components/shared/components/PricingsTable/hooks/usePricingsTable';
import { PricingRowActionType } from '@/components/shared/components/PricingsTable/types/PricingRowActionType';
import { SizedIconWithCircle } from '@/components/shared/components/SizedIconWithCircle';
import { Text } from '@/components/shared/components/Text';
import { useTranslation } from '@/components/shared/hooks/useTranslation';
import { useCustomTheme } from '@/hooks/useCustomTheme';
import { useUser } from '@/hooks/useUser';

interface PricingTableRowProps {
  pricing: Pricing;
  lenders: Lender[];
  handleRowPricingAction: (pricing: Pricing, pricingAction: PricingRowActionType) => void;
  showInsurances: boolean;
  agentInLenderContext?: AgentInLenderContext[];
  selectionProps?: SelectionProps;
  editModeAllowed?: boolean;
  allowedRemovePricingFromAgent?: boolean;
  forceEnabledRow?: boolean;
}

export interface SelectionProps {
  isSelected: boolean;
  setIsSelected: (pricingId: string) => void;
}

export const PricingTableRow = ({
  pricing,
  lenders,
  handleRowPricingAction,
  showInsurances,
  agentInLenderContext,
  selectionProps,
  editModeAllowed = false,
  allowedRemovePricingFromAgent = false,
  forceEnabledRow = false,
}: PricingTableRowProps) => {
  const { palette } = useCustomTheme();
  const { classes } = usePricingsTable({});
  const { userEntity } = useUser();
  const navigate = useNavigate();
  const { getTextIdTranslation } = useTranslation();

  const availableInsurances = useMemo(() => {
    if (!pricing.ranges) return [];
    const availableInsurances: PricingInsurance[] = [];
    for (const range of pricing.ranges) {
      if (!range.availableInsurances) continue;
      for (const insurance of range.availableInsurances) {
        if (availableInsurances.some((i) => i.type === insurance.type && i.provider === insurance.provider)) continue;
        availableInsurances.push(insurance);
      }
    }
    return availableInsurances;
  }, [pricing]);

  if (!userEntity) {
    return null;
  }
  const colHeight = 54;
  const currentPaymentMethod = pricing.paymentMethodTypes;

  const actions: ActionsOption[] = [
    {
      display: <Text tId={'SEE_PRICING_DETAIL'} />,
      iconName: 'open_in_new',
      onClick: () => handleRowPricingAction(pricing, PricingRowActionType.SEE_PRICING_DETAIL),
    },
    ...(editModeAllowed
      ? [
          {
            display: <Text tId={'OPEN_PRICING_EDITOR'} />,
            iconName: 'open_in_new',
            onClick: () => navigate(`/pricing/${pricing.id}`),
          },
          ...(allowedRemovePricingFromAgent
            ? [
                {
                  display: <Text tId={'REMOVE_PRICING_FROM_AGENT'} />,
                  iconName: 'delete',
                  onClick: () => handleRowPricingAction(pricing, PricingRowActionType.REMOVE_PRICING_FROM_AGENT),
                },
              ]
            : []),
        ]
      : []),
  ];

  const lenderNames = getLendersFromPricing(pricing);

  const allowedLenderNames = useMemo(() => {
    const merchantLenderNames = lenders.map((lender) => lender.name);
    return lenderNames.filter((lender) => merchantLenderNames.includes(lender));
  }, [pricing, lenders]);

  const disableLender = (lenderName: LenderName) => {
    const lenderHasCredentials = !!agentInLenderContext?.some((credential) => {
      const lenderNameByCredential = LenderMap.fromLenderId(credential.lenderId);
      if (credential.product === pricing.product && credential.program === pricing.program && lenderNameByCredential)
        return getLendersFromPricing(pricing)
          .filter((pricingLenderName) => pricingLenderName === lenderName)
          .includes(lenderNameByCredential);
    });
    return (!lenderHasCredentials || !allowedLenderNames.includes(lenderName)) && !forceEnabledRow;
  };

  const disabled =
    (lenderNames?.every((lenderName) => disableLender(lenderName) === true) || allowedLenderNames.length === 0) &&
    !forceEnabledRow;

  return (
    <>
      <TableRow
        key={pricing.id}
        className={classes.root}
        style={{
          backgroundColor: disabled ? palette.grey[50] : 'inherit',
        }}
      >
        {!userEntity.isRoleMerchantChannelOrAgent() && (
          <TableCell align={'left'} height={colHeight}>
            <Box p={1} display={'flex'} alignItems={'center'} justifyContent={'center'}>
              {selectionProps ? (
                <Checkbox
                  checked={selectionProps.isSelected}
                  onChange={() => selectionProps.setIsSelected(pricing.id)}
                />
              ) : (
                <Box position={'relative'}>
                  <SizedIconWithCircle
                    variant={'small'}
                    backgroundColor={palette.secondary.light}
                    iconColor={palette.secondary.contrastText}
                    customBorderRadius={100}
                    iconName={IconByAllowedPaymentMethodType(pricing.paymentMethodTypes[0])}
                    copyToClipboard={{
                      value: pricing.id,
                      textId: 'COPY_PRICING_ID',
                    }}
                  />
                  {pricing.paymentMethodTypes.length > 1 && (
                    <Box
                      position={'absolute'}
                      top={-6}
                      right={-6}
                      fontSize={9}
                      display={'flex'}
                      justifyContent={'center'}
                      alignItems={'center'}
                      width={14}
                      height={14}
                      borderRadius={14}
                      bgcolor={palette.grey[200]}
                      color={palette.getContrastText(palette.grey[200])}
                    >
                      {`+${pricing.paymentMethodTypes.length - 1}`}
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          </TableCell>
        )}
        <TableCell align={'left'} height={colHeight}>
          <Font variant={'body2'} style={{ color: disabled ? palette.grey[400] : palette.text.primary }}>
            {pricing.name}
          </Font>
        </TableCell>
        <TableCell align={'left'} height={colHeight}>
          <Font variant={'body2'} style={{ color: disabled ? palette.grey[400] : palette.text.primary }}>
            {pricing.paymentMethodTypes && pricing.paymentMethodTypes.length > 0 && (
              <Text tId={pricing.paymentMethodTypes[0]} />
            )}
          </Font>
        </TableCell>
        {!userEntity.isRoleMerchantChannelOrAgent() && (
          <TableCell align={'left'} height={colHeight}>
            <Font variant={'body2'} style={{ color: disabled ? palette.grey[400] : palette.text.primary }}>
              <Text tId={pricing.program} />
            </Font>
          </TableCell>
        )}
        <TableCell align={'left'} height={colHeight}>
          <Font variant={'body2'} style={{ color: disabled ? palette.grey[400] : palette.text.primary }}>
            {`${formatMoney({ value: pricing.rangePrincipalMin, currency: pricing.currency })} - ${formatMoney({
              value: pricing.rangePrincipalMax,
              currency: pricing.currency,
            })}`}
          </Font>
        </TableCell>
        <TableCell align={'left'} height={colHeight}>
          <Font variant={'body2'} style={{ color: disabled ? palette.grey[400] : palette.text.primary }}>
            {pricing.rangePeriodMin === null && pricing.rangePeriodMax === null
              ? '-'
              : `${pricing.rangePeriodMin?.toFixed(0)} - ${pricing.rangePeriodMax?.toFixed(0)}`}
          </Font>
        </TableCell>
        {showInsurances && (
          <TableCell align={'left'} height={colHeight}>
            <Box display={'flex'} minWidth={'160px'} flexWrap={'wrap'} gridGap={4}>
              {availableInsurances.map((insurance) => (
                <PricingInsuranceChip key={`${insurance.type}-${insurance.provider}`} insurance={insurance} />
              ))}
              {availableInsurances.length === 0 && (
                <Font variant={'body2'} color={'textPrimary'}>
                  {currentPaymentMethod.includes(PaymentMethodType.AUTO_LOAN)
                    ? getTextIdTranslation('NO_AVAILABLE_INSURANCES')
                    : '-'}
                </Font>
              )}
            </Box>
          </TableCell>
        )}
        <TableCell align={'left'} height={colHeight}>
          <Box display={'flex'} gridGap={4}>
            {lenderNames
              .sort((a, b) => (a > b ? 1 : -1))
              .map((lenderName, i) => {
                const disabledLender = disableLender(lenderName);
                return (
                  <Box key={i} style={{ filter: disabledLender ? 'grayscale(100%) opacity(30%)' : 'none' }}>
                    <LenderIconAvatar transparent key={`${pricing.name}-${lenderName}`} lenderName={lenderName} />
                  </Box>
                );
              })}
          </Box>
        </TableCell>
        <TableCell align={'right'} height={colHeight}>
          <MoreActionsMenuWithIcon actionsOptionsArray={actions} />
        </TableCell>
      </TableRow>
    </>
  );
};
